import React, { useRef, useState } from "react";
import { Box, Button, CircularProgress, Grid, Typography } from "@mui/material";
import {
  CardNumberElement,
  CardExpiryElement,
  CardCvcElement,
  useElements,
  useStripe,
} from "@stripe/react-stripe-js";
import api from "../../../services/api";

const elementStyle = {
  base: {
    fontSize: "16px",
    color: "#000",
    "::placeholder": { color: "#999" },
  },
  invalid: { color: "#e53935" },
};

const CardPaymentForm = ({
  onPaymentSuccess,
  amount,
  metadata,
  setErrorMessage,
  canProceed,
  onBlockedAttempt,
}) => {
  const stripe = useStripe();
  const elements = useElements();

  const expiryRef = useRef(null);
  const cvcRef = useRef(null);

  const [isCardComplete, setIsCardComplete] = useState(false);
  const [isExpiryComplete, setIsExpiryComplete] = useState(false);
  const [isCvcComplete, setIsCvcComplete] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);

  const [cardError, setCardError] = useState("");
  const [expiryError, setExpiryError] = useState("");
  const [cvcError, setCvcError] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!stripe || !elements) return;

    if (!canProceed) {
      onBlockedAttempt?.();
      return;
    }

    setIsProcessing(true);
    setErrorMessage("");

    try {
      const { data } = await api.post("/payments/create-intent", {
        amount: Math.round(amount * 100),
        metadata,
      });

      const result = await stripe.confirmCardPayment(data.clientSecret, {
        payment_method: {
          card: elements.getElement(CardNumberElement),
          billing_details: {
            name: metadata.name,
            email: metadata.email,
          },
        },
      });

      if (result.error) {
        const codeToMessage = {
          card_declined: "Your card was declined. Please try another.",
          expired_card: "Your card has expired. Use a different one.",
          incorrect_cvc: "The CVC code is incorrect. Please double-check.",
          processing_error: "Something went wrong while processing your card.",
          insufficient_funds: "Not enough funds on your card.",
          incorrect_zip: "The ZIP/postal code entered is incorrect.",
          authentication_required: "This card needs extra authentication.",
        };
        setErrorMessage(
          codeToMessage[result.error.code] || result.error.message
        );
      } else if (result.paymentIntent.status === "succeeded") {
        await onPaymentSuccess({
          type: "stripe",
          paymentIntent: result.paymentIntent,
        });
      }
    } catch (err) {
      setErrorMessage(
        err?.response?.data?.error || "Payment failed. Try again."
      );
    } finally {
      setIsProcessing(false);
    }
  };

  const isFormValid =
    isCardComplete && isExpiryComplete && isCvcComplete && !isProcessing;

  return (
    <form onSubmit={handleSubmit}>
      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={12}>
          <Typography variant="body2" gutterBottom>
            Card Number
          </Typography>
          <Box sx={{ border: "1px solid #ccc", borderRadius: 2, p: 1.5 }}>
            <CardNumberElement
              options={{ style: elementStyle }}
              onChange={(e) => {
                setIsCardComplete(e.complete);
                setCardError(e.error?.message || "");
                if (e.complete && expiryRef.current) expiryRef.current.focus();
              }}
            />
          </Box>
          {cardError && (
            <Typography variant="caption" color="error" mt={0.5}>
              {cardError}
            </Typography>
          )}
        </Grid>

        <Grid item xs={6}>
          <Typography variant="body2" gutterBottom>
            Expiry
          </Typography>
          <Box sx={{ border: "1px solid #ccc", borderRadius: 2, p: 1.5 }}>
            <CardExpiryElement
              options={{ style: elementStyle }}
              onReady={(el) => (expiryRef.current = el)}
              onChange={(e) => {
                setIsExpiryComplete(e.complete);
                setExpiryError(e.error?.message || "");
                if (e.complete && cvcRef.current) cvcRef.current.focus();
              }}
            />
          </Box>
          {expiryError && (
            <Typography variant="caption" color="error" mt={0.5}>
              {expiryError}
            </Typography>
          )}
        </Grid>

        <Grid item xs={6}>
          <Typography variant="body2" gutterBottom>
            CVC
          </Typography>
          <Box sx={{ border: "1px solid #ccc", borderRadius: 2, p: 1.5 }}>
            <CardCvcElement
              options={{ style: elementStyle }}
              onReady={(el) => (cvcRef.current = el)}
              onChange={(e) => {
                setIsCvcComplete(e.complete);
                setCvcError(e.error?.message || "");
              }}
            />
          </Box>
          {cvcError && (
            <Typography variant="caption" color="error" mt={0.5}>
              {cvcError}
            </Typography>
          )}
        </Grid>
      </Grid>

      <Button
        type="submit"
        fullWidth
        size="large"
        variant="contained"
        color="primary"
        disabled={!isFormValid}
        startIcon={
          isProcessing ? <CircularProgress size={18} color="inherit" /> : null
        }>
        {isProcessing ? "Processing..." : "Confirm and Pay"}
      </Button>
    </form>
  );
};

export default CardPaymentForm;
